import { FileSpreadsheet, FileText, FileImage, File, Download, FileType, Loader2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { formatDateTime } from '../../lib/utils';
import type { Attachment } from '../../types';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
const PDF_TYPES = ['application/pdf'];
const EXCEL_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const WORD_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const PPT_TYPES = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
const OFFICE_TYPES = [...EXCEL_TYPES, ...WORD_TYPES, ...PPT_TYPES];

function getFileIcon(mimeType: string) {
  if (IMAGE_TYPES.includes(mimeType)) return <FileImage size={20} className="text-green-500" />;
  if (PDF_TYPES.includes(mimeType)) return <FileText size={20} className="text-red-500" />;
  if (EXCEL_TYPES.includes(mimeType)) return <FileSpreadsheet size={20} className="text-emerald-600" />;
  if (WORD_TYPES.includes(mimeType)) return <FileType size={20} className="text-blue-600" />;
  if (PPT_TYPES.includes(mimeType)) return <FileText size={20} className="text-orange-500" />;
  return <File size={20} className="text-gray-400" />;
}

function useAuthBlobUrl(attachmentId: string, mimeType: string, enabled: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
      setUrl(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/attachments/${attachmentId}?inline=1`, {
      credentials: 'include',
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); })
      .then((buf) => {
        if (cancelled) return;
        const blob = new Blob([buf], { type: mimeType });
        const objectUrl = URL.createObjectURL(blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = objectUrl;
        setUrl(objectUrl);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [attachmentId, mimeType, enabled]);

  return { url, loading, error };
}

function getOfficeLabel(mimeType: string) {
  if (EXCEL_TYPES.includes(mimeType)) return 'Excel';
  if (WORD_TYPES.includes(mimeType)) return 'Word';
  if (PPT_TYPES.includes(mimeType)) return 'PowerPoint';
  return 'Office';
}

export function AttachmentPreview({ attachment }: { attachment: Attachment }) {
  const [expanded, setExpanded] = useState(false);
  const isImage = IMAGE_TYPES.includes(attachment.mimeType);
  const isPdf = PDF_TYPES.includes(attachment.mimeType);
  const isOffice = OFFICE_TYPES.includes(attachment.mimeType);
  const canPreview = isImage || isPdf || isOffice;

  const { url: blobUrl, loading, error } = useAuthBlobUrl(attachment.id, attachment.mimeType, expanded && (isImage || isPdf));

  // Authenticated download URL helper
  const handleDownload = () => {
    fetch(`/api/attachments/${attachment.id}`, {
      credentials: 'include',
    })
      .then((r) => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = attachment.fileName;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => {});
  };

  return (
    <div className="rounded-lg border bg-white dark:bg-dark-card dark:border-dark-border overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <span className="shrink-0">{getFileIcon(attachment.mimeType)}</span>
        <span className="truncate text-sm font-medium dark:text-dark-text max-w-[200px] sm:max-w-none">{attachment.fileName}</span>
        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0 dark:text-dark-muted">{(attachment.sizeBytes / 1024).toFixed(0)} KB</span>
        {attachment.uploadedBy?.name && <span className="text-xs text-gray-400 whitespace-nowrap shrink-0 dark:text-dark-muted">— {attachment.uploadedBy.name}{attachment.createdAt ? `, ${formatDateTime(attachment.createdAt)}` : ''}</span>}
        {isOffice && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600 shrink-0 dark:bg-blue-900/30 dark:text-blue-400">{getOfficeLabel(attachment.mimeType)}</span>}
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          {canPreview && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
            >
              {expanded ? 'Ascunde' : 'Preview'}
            </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-dark-muted dark:hover:bg-dark-border dark:hover:text-dark-text"
            title="Descarcă"
          >
            <Download size={16} />
          </button>
        </div>
      </div>

      {expanded && (isImage || isPdf) && loading && (
        <div className="border-t p-4 flex items-center justify-center dark:border-dark-border">
          <Loader2 size={24} className="animate-spin text-blue-500" />
          <span className="ml-2 text-sm text-gray-500 dark:text-dark-muted">Se încărcă...</span>
        </div>
      )}

      {expanded && (isImage || isPdf) && error && (
        <div className="border-t p-4 text-center dark:border-dark-border">
          <p className="text-sm text-red-500">Nu s-a putut încărca fișierul.</p>
          <button type="button" onClick={handleDownload} className="mt-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">
            Descarcă
          </button>
        </div>
      )}

      {expanded && isImage && blobUrl && (
        <div className="border-t p-2 dark:border-dark-border">
          <img
            src={blobUrl}
            alt={attachment.fileName}
            className="max-h-96 w-full rounded object-contain"
          />
        </div>
      )}

      {expanded && isPdf && blobUrl && (
        <div className="border-t p-2 dark:border-dark-border">
          <object
            data={`${blobUrl}#toolbar=1`}
            type="application/pdf"
            className="h-96 w-full rounded"
          >
            <p className="p-4 text-center text-sm text-gray-500 dark:text-dark-muted">
              PDF-ul nu poate fi afișat în browser.{' '}
              <button type="button" onClick={handleDownload} className="text-blue-600 underline">Descarcă</button>
            </p>
          </object>
        </div>
      )}

      {expanded && isOffice && (
        <div className="border-t p-4 dark:border-dark-border">
          <div className="flex flex-col items-center gap-3 rounded-lg bg-gray-50 py-6 dark:bg-dark-border/50">
            {getFileIcon(attachment.mimeType)}
            <p className="text-sm font-medium text-gray-700 dark:text-dark-text">{attachment.fileName}</p>
            <p className="text-xs text-gray-400 dark:text-dark-muted">Fișier {getOfficeLabel(attachment.mimeType)} — {(attachment.sizeBytes / 1024).toFixed(0)} KB</p>
            <button
              type="button"
              onClick={handleDownload}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Descarcă pentru a vizualiza
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
